import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ViewportChangedEvent, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { OrganizationService } from '../../../services/organization.service';
import { VisibilityGroupInfo } from '../../../models/visibilities-info';
import { TranslationService } from '../../../../shared/services/translation.service';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { LoadingSpinnerCellComponent }
	from '../../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { CommonColDef } from '../../../../shared/datacenter/column-defs';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { ColumnType } from '../../../../shared/datacenter/column-type';

const resizeObservable = fromEvent(globalThis, 'resize');

@Component({
	selector: 'copo-organization-card-informational-visibilities',
	templateUrl: './organization-card-informational-visibilities.component.html',
	styleUrl: './organization-card-informational-visibilities.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [TranslateModule, AgGridModule]
})
export class OrganizationCardInformationalVisibilitiesComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	infoVisibilities?: VisibilityGroupInfo[];
	isLoading = true;
	gridApi?: GridApi<VisibilityGroupInfo>;
	resizeSub?: Subscription;
	readonly agGridModules = clientSideAgGridModules;
	gridInformationalVisibilities: CommonGridOptions<VisibilityGroupInfo>;

	private orgId?: string;
	private orgServiceSub: Subscription = Subscription.EMPTY;
	private readonly routeSub: Subscription = Subscription.EMPTY;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly translationService: TranslationService
	) {
		this.gridInformationalVisibilities = new CommonGridOptions<VisibilityGroupInfo>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.infoVisibilities,
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			headerHeight: 70,
			domLayout: 'autoHeight',
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			columnDefs: [
				new CommonColDef<VisibilityGroupInfo, string>({
					headerName: this.translationService.getTR('settings.organizations.name'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'bocClient.name',
					sort: 'asc',
					comparator: (valueA, valueB) =>
						!valueA ? -1 : !valueB ? 1 : valueA.toString().localeCompare(valueB.toString()),
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				}),
				new CommonColDef<VisibilityGroupInfo>({
					headerName: this.translationService.getTR('settings.business-id'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'bocClient.businessId',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						return params.data.bocClient.businessId.padStart(10, '0');
					},
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				}),
				new CommonColDef<VisibilityGroupInfo>({
					headerName: this.translationService.getTR('settings.organizations.role'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'role',
					filter: true,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			]
		});
		if (this.activatedRoute.parent === null) {
			return;
		}
		this.routeSub = this.activatedRoute.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.orgId = idString;
		});
		if (this.orgId) {
			this.getInfoVisibilities(this.orgId);
		}
	}

	getInfoVisibilities(orgId: string) {
		this.orgServiceSub = this.organizationService.getOrganizationVisibilities(
			orgId
		)
			.subscribe({
				next: orgVisibilities => {
					if (this.gridApi) {
						if (orgVisibilities.results[0]?.visibilityGroupInfo) {
							this.infoVisibilities = orgVisibilities.results[0].visibilityGroupInfo;
							for (const visibility of this.infoVisibilities) {
								if (visibility.role === 'Enduser' && visibility.isPayer === 0
									&& visibility.isShipTo === 0 && visibility.isSoldTo === 0) {
									visibility.role = this.translationService.getTR('eoffer.enduser');
								} else if (visibility.isPayer === 1 && visibility.isShipTo === 1
									&& visibility.isSoldTo === 1) {
									visibility.role = this.translationService.getTR('settings.any');
								} else if (visibility.isPayer === 1 && visibility.isShipTo === 0
									&& visibility.isSoldTo === 0) {
									visibility.role = this.translationService.getTR('commercial-documents.payer');
								} else if (visibility.isSoldTo === 1 && visibility.isPayer === 0
									&& visibility.isShipTo === 0) {
									visibility.role = this.translationService.getTR('commercial-documents.soldto');
								} else if (visibility.isSoldTo === 0 && visibility.isPayer === 0
									&& visibility.isShipTo === 1) {
									visibility.role = this.translationService.getTR('batch.shipto');
								}
							}
							this.gridApi.setFilterModel(null);
							this.gridApi.setGridOption('rowData', this.infoVisibilities);
						} else {
							this.gridApi.setFilterModel(null);
							this.gridApi.setGridOption('rowData', []);
						}
					}
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('Error from http get organization visibilities', err);
					this.isLoading = false;
				}
			});
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	ngOnDestroy() {
		this.orgServiceSub.unsubscribe();
		this.routeSub.unsubscribe();
		if (this.resizeSub) {
			this.resizeSub.unsubscribe();
		}
	}
}
